import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { EUROSTEEL_SIGNATURE, DULUX_POWDERCOAT } from "@/data/finishes";

const Swatch = ({ name, image, index, aspect = "4/3" }) => (
  <motion.figure
    data-testid={`finish-swatch-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{
      duration: 0.55,
      delay: (index % 6) * 0.04,
      ease: [0.19, 1, 0.22, 1],
    }}
    className="group flex flex-col overflow-hidden rounded-sm border border-[#0a0a0c]/10 bg-white transition-all duration-500 hover:border-[#c6a87c] hover:shadow-[0_0_28px_-8px_rgba(198,168,124,0.35)]"
  >
    <div
      className="relative w-full overflow-hidden bg-[#f4f2ee]"
      style={{ aspectRatio: aspect }}
    >
      <img
        src={image}
        alt={name}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.04]"
      />
    </div>
    <figcaption className="border-t border-black/5 px-2 py-1.5 text-center">
      <span className="font-serif-display text-[9px] font-bold uppercase leading-tight tracking-tight text-[#0a0a0c] lg:text-[10px]">
        {name}
      </span>
    </figcaption>
  </motion.figure>
);

export default function Finishes() {
  return (
    <div data-testid="finishes-page" className="min-h-screen bg-white text-[#0a0a0c]">
      {/* Header */}
      <section className="mx-auto max-w-[1400px] px-6 pb-10 pt-40 text-center lg:px-16 lg:pt-48">
        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
          data-testid="finishes-heading"
          className="font-serif-display text-5xl font-bold uppercase leading-[0.98] tracking-tight text-[#0a0a0c] lg:text-7xl"
        >
          Colour Finishes.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.14, ease: [0.19, 1, 0.22, 1] }}
          className="mt-5 font-tech text-[11px] uppercase tracking-[0.4em] text-[#c6a87c]"
        >
          Eurosteel Signature · Dulux Powdercoat · Custom Match
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.22, ease: [0.19, 1, 0.22, 1] }}
          className="mx-auto mt-6 max-w-2xl font-body text-[14px] leading-[1.75] text-[#0a0a0c]/70 lg:text-[15px]"
        >
          Every steel window, door and entrance can be finished in our signature
          Eurosteel palette, the full Dulux Duratec powdercoat range, or a bespoke
          colour match to your specification.
        </motion.p>
      </section>

      {/* Eurosteel Signature Range */}
      <section
        data-testid="finishes-signature"
        className="mx-auto max-w-[1400px] px-6 pb-24 lg:px-16"
      >
        <div className="mb-10 border-t border-[#0a0a0c]/10 pt-14">
          <span className="font-tech text-[10px] uppercase tracking-[0.5em] text-[#c6a87c]">
            — Signature Range
          </span>
          <h2 className="mt-4 font-serif-display text-3xl font-bold uppercase tracking-tight text-[#0a0a0c] lg:text-4xl">
            Eurosteel Signature Colours
          </h2>
          <p className="mt-3 max-w-2xl font-body text-[13px] leading-relaxed text-[#0a0a0c]/70 lg:text-[14px]">
            The house palette — every swatch below is a photograph of the actual
            hand-finished sample, so the metallic, brushed and textured surfaces
            read exactly as they will on your steelwork.
          </p>
        </div>
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 lg:gap-3">
          {EUROSTEEL_SIGNATURE.map((c, i) => (
            <Swatch key={c.name} {...c} aspect="1/1" index={i} />
          ))}
        </div>
      </section>

      {/* Dulux Powdercoat range */}
      <section
        data-testid="finishes-dulux"
        className="mx-auto max-w-[1400px] px-6 pb-24 lg:px-16"
      >
        <div className="mb-10 border-t border-[#0a0a0c]/10 pt-14">
          <span className="font-tech text-[10px] uppercase tracking-[0.5em] text-[#c6a87c]">
            — Powdercoat Selector
          </span>
          <h2 className="mt-4 font-serif-display text-3xl font-bold uppercase tracking-tight text-[#0a0a0c] lg:text-4xl">
            Dulux Powdercoat Range
          </h2>
          <p className="mt-3 max-w-2xl font-body text-[13px] leading-relaxed text-[#0a0a0c]/70 lg:text-[14px]">
            The full Dulux Duralloy, Duralloy +Plus, Pearlescent and Protexture
            libraries — each swatch cropped from the manufacturer&apos;s selector
            sheet so pearl and textured finishes stay true.
          </p>
        </div>

        {DULUX_POWDERCOAT.map((group) => (
          <div key={group.category} className="mb-16">
            <h3
              data-testid={`finishes-group-${group.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="mb-5 font-tech text-[11px] uppercase tracking-[0.4em] text-[#0a0a0c]/60"
            >
              {group.category}
            </h3>
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 lg:gap-3">
              {group.colours.map((c, i) => (
                <Swatch key={`${group.category}-${c.name}`} {...c} index={i} />
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Custom colour match panel */}
      <section className="mx-auto max-w-[1400px] px-6 pb-32 lg:px-16">
        <div
          data-testid="finishes-custom-match"
          className="relative overflow-hidden rounded-sm border border-[#0a0a0c]/10 bg-gradient-to-br from-[#0a0a0c] to-[#1a1a1c] p-10 text-white lg:p-16"
        >
          <span
            aria-hidden
            className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[#c6a87c]/25 blur-3xl"
          />
          <div className="relative max-w-2xl">
            <span className="font-tech text-[10px] uppercase tracking-[0.5em] text-[#c6a87c]">
              — Custom Colour Match
            </span>
            <h2 className="mt-4 font-serif-display text-3xl font-bold uppercase tracking-tight text-white lg:text-4xl">
              Can&apos;t find the shade you want?
            </h2>
            <p className="mt-5 font-body text-[14px] leading-relaxed text-white/75 lg:text-[15px]">
              Bring us a swatch, a hex code or a competitor&apos;s colour and we&apos;ll
              custom-match it in-house. Every Eurosteel profile can be powder-coated
              to any RAL, BS or Resene reference — including two-tone frames where
              the interior and exterior read differently.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="finishes-cta-enquire"
                className="rounded-full bg-[#c6a87c] px-10 py-3 font-tech text-[11px] uppercase tracking-[0.35em] text-[#0a0a0c] transition-all duration-500 hover:bg-[#d8bd93]"
              >
                Request a Sample
              </Link>
              <Link
                to="/products"
                className="rounded-full border border-white/25 px-10 py-3 font-tech text-[11px] uppercase tracking-[0.35em] text-white/80 transition-all duration-500 hover:border-[#c6a87c] hover:text-[#c6a87c]"
              >
                Browse Products
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
